import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  FlatList,
  Modal,
  TextInput,
  ActivityIndicator,
  RefreshControl,
  Alert,
  Dimensions,
  Image,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

import axios from 'axios';
import { useFocusEffect } from '@react-navigation/native';
const { width, height } = Dimensions.get('window');

const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token'); if (t) c.headers.Authorization = 'Bearer ' + t; const bid = await AsyncStorage.getItem('company_id'); if (bid) c.params = { ...c.params, company_id: parseInt(bid) }; } catch {} return c; }, e => Promise.reject(e));
const safeData = r => (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];
const EP = { employees: API_BASE_URL + '/api/data/employees',
  hireCandidate: API_BASE_URL + '/api/recruitment/hire',
  respondOffer: (id) => API_BASE_URL + '/api/recruitment/offers/' + id + '/respond', offers: API_BASE_URL + '/api/data/job_offers', applications: API_BASE_URL + '/api/data/applications', companyProfile: API_BASE_URL + '/api/data/company_profiles', hire: API_BASE_URL + '/api/recruitment/hire', recruitDashboard: API_BASE_URL + '/api/recruitment/dashboard' };

export default function HiredPipelineScreen({ navigation, route }) {
  const jobId = route?.params?.jobId || null;
  const jobTitle = route?.params?.jobTitle || '';
  // When AcceptedOffersScreen / AcceptedOfferDetailsScreen forwards a freshly
  // hired candidate, the route carries identifiers we use to highlight and
  // auto-scroll to that row in the list.
  const focusApplicantId   = route?.params?.focusApplicantId   || null;
  const focusEmployeeEmail = route?.params?.focusEmployeeEmail || null;
  const focusEmployeeId    = route?.params?.focusEmployeeId    || null;
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [companyId, setCompanyId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTab, setSelectedTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('start_date');
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [realTimeData, setRealTimeData] = useState({});

  // Real-time tracking metrics
  const trackingMetrics = [
    {
      id: 'first_day_completed',
      name: 'First Day Completed',
      description: 'Employee has completed their first day successfully',
      icon: 'calendar-check',
      color: '#10B981',
    },
    {
      id: 'onboarding_progress',
      name: 'Onboarding Progress',
      description: 'Progress through onboarding program',
      icon: 'school',
      color: '#3B82F6',
    },
    {
      id: 'team_integration',
      name: 'Team Integration',
      description: 'Integration with team and colleagues',
      icon: 'account-group',
      color: '#8B5CF6',
    },
    {
      id: 'performance_tracking',
      name: 'Performance Tracking',
      description: 'Early performance indicators and feedback',
      icon: 'trending-up',
      color: '#F59E0B',
    },
    {
      id: 'satisfaction_score',
      name: 'Satisfaction Score',
      description: 'Employee satisfaction and engagement',
      icon: 'heart',
      color: '#EF4444',
    },
  ];

  // Mock employee data - 2 hired employees
  const generateMockEmployees = () => {
    const names = ['Sarah Johnson', 'Michael Chen'];

    const positions = ['Senior Software Engineer', 'Product Manager'];

    const departments = ['Engineering', 'Product'];

    const managers = ['John Smith - Engineering Director', 'Lisa Brown - VP Product'];

    const buddies = ['Alice Cooper - Senior Developer', 'Bob Wilson - Product Lead'];

    const locations = ['London, UK', 'Manchester, UK'];

    const skills = [
      ['React', 'Node.js', 'TypeScript', 'AWS', 'Docker', 'Kubernetes', 'Python', 'GraphQL'],
      ['Product Strategy', 'User Research', 'Data Analysis', 'Agile', 'Scrum', 'Roadmapping', 'A/B Testing', 'SQL'],
    ];

    const mockEmployees = [];
    
    for (let i = 1; i <= 2; i++) {
      const name = names[i - 1];
      const position = positions[i - 1];
      const department = departments[i - 1];
      const manager = managers[i - 1];
      const buddy = buddies[i - 1];
      const location = locations[i - 1];
      const employeeSkills = skills[i - 1];
      
      // Generate employment details
      const employeeId = `EMP${String(i).padStart(4, '0')}`;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - Math.floor(Math.random() * 30)); // Started within last 30 days
      const hireDate = new Date(startDate);
      hireDate.setDate(hireDate.getDate() - Math.floor(Math.random() * 7)); // Hired 0-7 days before start
      
      // Generate compensation details
      const baseSalary = i === 1 ? 95000 : 85000; // Senior Engineer vs Product Manager
      const bonus = Math.floor(baseSalary * (Math.random() * 0.25 + 0.15)); // 15-40% bonus
      const stockOptions = Math.floor(Math.random() * 40000) + 20000; // £20k-£60k
      const totalPackage = baseSalary + bonus + stockOptions;
      
      // Generate real-time tracking data
      const daysWorked = Math.ceil((new Date() - startDate) / (1000 * 60 * 60 * 24));
      const onboardingProgress = Math.min(100, Math.floor((daysWorked / 30) * 100)); // 30-day onboarding
      const teamIntegrationScore = Math.floor(Math.random() * 30) + 70; // 70-100%
      const performanceScore = Math.floor(Math.random() * 25) + 75; // 75-100%
      const satisfactionScore = Math.floor(Math.random() * 20) + 80; // 80-100%
      
      // Generate onboarding milestones
      const onboardingMilestones = [
        {
          id: 'first_day',
          name: 'First Day Orientation',
          completed: daysWorked >= 1,
          completedDate: daysWorked >= 1 ? new Date(startDate.getTime() + 24 * 60 * 60 * 1000).toISOString() : null,
        },
        {
          id: 'week_one',
          name: 'Week 1 Check-in',
          completed: daysWorked >= 7,
          completedDate: daysWorked >= 7 ? new Date(startDate.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString() : null,
        },
        {
          id: 'month_one',
          name: '30-Day Review',
          completed: daysWorked >= 30,
          completedDate: daysWorked >= 30 ? new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString() : null,
        },
        {
          id: 'probation',
          name: 'Probation Review',
          completed: daysWorked >= 90,
          completedDate: daysWorked >= 90 ? new Date(startDate.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString() : null,
        },
      ];

      const completedMilestones = onboardingMilestones.filter(m => m.completed).length;
      
      // Generate team integration details
      const teamMembers = [
        'Alice Cooper', 'Bob Smith', 'Carol Johnson', 'David Wilson', 
        'Eva Martinez', 'Frank Brown', 'Grace Lee', 'Henry Davis',
      ];
      
      const assignedTeamMembers = teamMembers.slice(0, Math.floor(Math.random() * 4) + 3); // 3-6 team members
      const metTeamMembers = assignedTeamMembers.slice(0, Math.floor(assignedTeamMembers.length * (teamIntegrationScore / 100)));
      
      // Generate performance indicators
      const performanceIndicators = [
        {
          id: 'task_completion',
          name: 'Task Completion Rate',
          value: Math.floor(Math.random() * 20) + 80, // 80-100%
          trend: Math.random() > 0.3 ? 'up' : 'stable',
        },
        {
          id: 'code_quality',
          name: i === 1 ? 'Code Quality Score' : 'Deliverable Quality',
          value: Math.floor(Math.random() * 15) + 85, // 85-100%
          trend: Math.random() > 0.2 ? 'up' : 'stable',
        },
        {
          id: 'collaboration',
          name: 'Collaboration Score',
          value: teamIntegrationScore,
          trend: Math.random() > 0.4 ? 'up' : 'stable',
        },
        {
          id: 'learning_progress',
          name: 'Learning Progress',
          value: onboardingProgress,
          trend: 'up',
        },
      ];
      
      // Generate recent activities
      const recentActivities = [
        {
          id: 'activity_1',
          type: 'milestone',
          title: daysWorked >= 7 ? 'Completed Week 1 Check-in' : 'Started First Week',
          description: daysWorked >= 7 ? 'Successfully completed first week review with manager' : 'Beginning first week of employment',
          timestamp: new Date(Date.now() - Math.random() * 24 * 60 * 60 * 1000).toISOString(),
          icon: 'calendar-check',
          color: '#10B981',
        },
        {
          id: 'activity_2',
          type: 'performance',
          title: i === 1 ? 'Code Review Completed' : 'Product Analysis Submitted',
          description: i === 1 ? 'First code review completed with positive feedback' : 'Market analysis report submitted and approved',
          timestamp: new Date(Date.now() - Math.random() * 48 * 60 * 60 * 1000).toISOString(),
          icon: 'check-circle',
          color: '#3B82F6',
        },
        {
          id: 'activity_3',
          type: 'team',
          title: 'Team Meeting Participation',
          description: 'Actively participated in team standup and planning meetings',
          timestamp: new Date(Date.now() - Math.random() * 72 * 60 * 60 * 1000).toISOString(),
          icon: 'account-group',
          color: '#8B5CF6',
        },
        {
          id: 'activity_4',
          type: 'training',
          title: 'Training Module Completed',
          description: 'Completed company security and compliance training',
          timestamp: new Date(Date.now() - Math.random() * 96 * 60 * 60 * 1000).toISOString(),
          icon: 'school',
          color: '#F59E0B',
        },
      ];
      
      // Generate feedback and reviews
      const managerFeedback = [
        {
          date: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000).toISOString(),
          rating: Math.floor(Math.random() * 2) + 4, // 4-5 stars
          feedback: i === 1 ? 
            'Sarah is showing excellent technical skills and is integrating well with the team. Her code quality is impressive for someone just starting.' :
            'Michael has brought great strategic thinking to our product discussions. His analytical approach is exactly what we needed.',
          reviewer: manager.split(' - ')[0],
        },
      ];
      
      const buddyFeedback = [
        {
          date: new Date(Date.now() - Math.random() * 5 * 24 * 60 * 60 * 1000).toISOString(),
          rating: Math.floor(Math.random() * 2) + 4, // 4-5 stars
          feedback: i === 1 ? 
            'Sarah asks great questions and is very proactive in learning our codebase. She\'s fitting in perfectly with the development team.' :
            'Michael is very collaborative and brings fresh perspectives to our product planning. Great addition to the team.',
          reviewer: buddy.split(' - ')[0],
        },
      ];
      
      // Generate equipment and setup status
      const equipmentSetup = {
        laptop: {
          model: i === 1 ? 'MacBook Pro 16"' : 'Dell XPS 15',
          status: 'delivered',
          setupComplete: true,
        },
        accounts: {
          email: true,
          slack: true,
          github: i === 1,
          jira: true,
          confluence: true,
        },
        access: {
          building: true,
          parking: true,
          systems: true,
          repositories: i === 1,
        },
      };
      
      // Generate goals and objectives
      const goals = [
        {
          id: 'goal_1',
          title: i === 1 ? 'Complete React Component Library' : 'Launch User Research Program',
          description: i === 1 ? 
            'Build and document reusable React components for the design system' :
            'Establish user research processes and conduct first user interviews',
          dueDate: new Date(Date.now() + Math.random() * 60 * 24 * 60 * 60 * 1000).toISOString(), // Next 60 days
          progress: Math.floor(Math.random() * 60) + 20, // 20-80%
          priority: 'high',
        },
        {
          id: 'goal_2',
          title: i === 1 ? 'Learn GraphQL Implementation' : 'Optimize Product Metrics Dashboard',
          description: i === 1 ? 
            'Master GraphQL schema design and resolver implementation' :
            'Improve product analytics dashboard for better decision making',
          dueDate: new Date(Date.now() + Math.random() * 90 * 24 * 60 * 60 * 1000).toISOString(), // Next 90 days
          progress: Math.floor(Math.random() * 40) + 10, // 10-50%
          priority: 'medium',
        },
      ];
      
      // Generate training progress
      const trainingModules = [
        {
          id: 'security',
          name: 'Security & Compliance',
          completed: true,
          completedDate: new Date(startDate.getTime() + 2 * 24 * 60 * 60 * 1000).toISOString(),
          score: Math.floor(Math.random() * 20) + 80,
        },
        {
          id: 'company_culture',
          name: 'Company Culture & Values',
          completed: true,
          completedDate: new Date(startDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString(),
          score: Math.floor(Math.random() * 15) + 85,
        },
        {
          id: 'technical',
          name: i === 1 ? 'Technical Architecture' : 'Product Management Fundamentals',
          completed: daysWorked >= 14,
          completedDate: daysWorked >= 14 ? new Date(startDate.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString() : null,
          score: daysWorked >= 14 ? Math.floor(Math.random() * 20) + 80 : null,
        },
        {
          id: 'advanced',
          name: i === 1 ? 'Advanced Development Practices' : 'Strategic Product Planning',
          completed: daysWorked >= 30,
          completedDate: daysWorked >= 30 ? new Date(startDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString() : null,
          score: daysWorked >= 30 ? Math.floor(Math.random() * 25) + 75 : null,
        },
      ];
      
      const completedTraining = trainingModules.filter(t => t.completed).length;
      
      // Generate real-time status
      const currentStatus = (() => {
        if (daysWorked < 7) return 'first_week';
        if (daysWorked < 30) return 'onboarding';
        if (daysWorked < 90) return 'probation';
        return 'established';
      })();
      
      const statusText = {
        'first_week': 'First Week',
        'onboarding': 'Onboarding',
        'probation': 'Probation Period',
        'established': 'Established Employee',
      };
      
      // Generate success indicators
      const successIndicators = [];
      if (performanceScore >= 90) successIndicators.push('High Performance');
      if (satisfactionScore >= 90) successIndicators.push('High Satisfaction');
      if (teamIntegrationScore >= 85) successIndicators.push('Great Team Fit');
      if (onboardingProgress >= 80) successIndicators.push('Fast Learner');
      if (completedMilestones >= 2) successIndicators.push('Milestone Achiever');
      
      // Generate risk factors
      const riskFactors = [];
      if (performanceScore < 80) riskFactors.push('Performance Concern');
      if (satisfactionScore < 75) riskFactors.push('Satisfaction Risk');
      if (teamIntegrationScore < 70) riskFactors.push('Integration Challenge');
      if (onboardingProgress < 60) riskFactors.push('Slow Progress');
      
      // Generate next milestones
      const nextMilestones = onboardingMilestones.filter(m => !m.completed).slice(0, 2);
      
      mockEmployees.push({
        id: `employee_${i}`,
        employeeId: employeeId,
        name: name,
        email: `${name.toLowerCase().replace(' ', '.')}@company.com`,
        phone: `+44 ${Math.floor(Math.random() * 9000) + 1000} ${Math.floor(Math.random() * 900000) + 100000}`,
        position: position,
        department: department,
        manager: manager,
        buddy: buddy,
        location: location,
        skills: employeeSkills,
        
        // Employment details
        startDate: startDate.toISOString(),
        hireDate: hireDate.toISOString(),
        daysWorked: daysWorked,
        currentStatus: currentStatus,
        statusText: statusText[currentStatus],
        
        // Compensation
        baseSalary: baseSalary,
        bonus: bonus,
        stockOptions: stockOptions,
        totalPackage: totalPackage,
        
        // Real-time tracking metrics
        onboardingProgress: onboardingProgress,
        teamIntegrationScore: teamIntegrationScore,
        performanceScore: performanceScore,
        satisfactionScore: satisfactionScore,
        overallScore: Math.floor((onboardingProgress + teamIntegrationScore + performanceScore + satisfactionScore) / 4),
        
        // Milestones and progress
        onboardingMilestones: onboardingMilestones,
        completedMilestones: completedMilestones,
        totalMilestones: onboardingMilestones.length,
        nextMilestones: nextMilestones,
        
        // Team integration
        assignedTeamMembers: assignedTeamMembers,
        metTeamMembers: metTeamMembers,
        teamIntegrationDetails: {
          totalMembers: assignedTeamMembers.length,
          metMembers: metTeamMembers.length,
          pendingIntroductions: assignedTeamMembers.length - metTeamMembers.length,
        },
        
        // Performance tracking
        performanceIndicators: performanceIndicators,
        recentActivities: recentActivities,
        managerFeedback: managerFeedback,
        buddyFeedback: buddyFeedback,
        
        // Equipment and setup
        equipmentSetup: equipmentSetup,
        setupComplete: Object.values(equipmentSetup.accounts).every(Boolean) && 
                      Object.values(equipmentSetup.access).every(Boolean),
        
        // Goals and training
        goals: goals,
        trainingModules: trainingModules,
        completedTraining: completedTraining,
        totalTraining: trainingModules.length,
        
        // Success and risk factors
        successIndicators: successIndicators,
        riskFactors: riskFactors,
        
        // Real-time data
        lastActivity: recentActivities[0].timestamp,
        lastSeen: new Date(Date.now() - Math.random() * 4 * 60 * 60 * 1000).toISOString(), // Last 4 hours
        isOnline: Math.random() > 0.3, // 70% chance online
        
        // Previous recruitment data
        recruitmentJourney: {
          applicationDate: new Date(hireDate.getTime() - Math.random() * 60 * 24 * 60 * 60 * 1000).toISOString(),
          totalDaysInPipeline: Math.floor(Math.random() * 45) + 30, // 30-75 days
          finalInterviewScore: Math.floor(Math.random() * 10) + 90,
          arDemoScore: Math.floor(Math.random() * 10) + 90,
          overallRating: Math.floor(Math.random() * 1) + 4, // 4-5 stars
        },
        
        // Tags and labels
        tags: (() => {
          const baseTags = ['Real-time Tracking'];
          if (currentStatus === 'first_week') baseTags.push('New Hire');
          if (performanceScore >= 90) baseTags.push('High Performer');
          if (satisfactionScore >= 90) baseTags.push('Highly Satisfied');
          if (teamIntegrationScore >= 90) baseTags.push('Team Player');
          if (onboardingProgress >= 90) baseTags.push('Fast Learner');
          return baseTags;
        })(),
        
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`,
      });
    }

    return mockEmployees.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));
  };

  const tabs = [
    { id: 'all', name: 'All', count: 2 },
    { id: 'first_week', name: 'First Week', count: 0 },
    { id: 'onboarding', name: 'Onboarding', count: 1 },
    { id: 'probation', name: 'Probation', count: 1 },
    { id: 'established', name: 'Established', count: 0 },
  ];

  const sortOptions = [
    { id: 'start_date', name: 'Start Date', icon: 'calendar-start' },
    { id: 'performance', name: 'Performance', icon: 'trending-up' },
    { id: 'satisfaction', name: 'Satisfaction', icon: 'heart' },
    { id: 'name', name: 'Name', icon: 'account' },
    { id: 'onboarding_progress', name: 'Onboarding Progress', icon: 'school' },
  ];

  useEffect(() => {
    loadEmployees();
    startRealTimeUpdates();
  }, []);

  // When AcceptedOffers→Hired forwards a candidate (focusApplicantId or
  // focusEmployeeEmail in route.params), find them in the loaded list and
  // surface them: select them, show a one-time confirmation toast/alert,
  // and optionally scroll the FlatList to their row. The screen still
  // shows everyone else — focus is additive, not a filter.
  const [focusedId, setFocusedId] = useState(null);
  const flatListRef = React.useRef(null);
  useEffect(() => {
    if (isLoading) return;
    if (!focusApplicantId && !focusEmployeeEmail && !focusEmployeeId) return;
    const match = employees.find(e =>
      (focusEmployeeEmail && e.email && String(e.email ?? '').toLowerCase() === String(focusEmployeeEmail).toLowerCase()) ||
      (focusApplicantId && String(e.applicationId) === String(focusApplicantId)) ||
      (focusEmployeeId  && String(e.employeeId)    === String(focusEmployeeId))
    );
    if (match && focusedId !== match.id) {
      setFocusedId(match.id);
      // Scroll to the row once layout is ready
      setTimeout(() => {
        const idx = employees.findIndex(e => e.id === match.id);
        if (idx >= 0 && flatListRef.current?.scrollToIndex) {
          try { flatListRef.current.scrollToIndex({ index: idx, animated: true, viewPosition: 0.2 }); } catch {}
        }
      }, 300);
    }
  }, [isLoading, employees.length, focusApplicantId, focusEmployeeEmail, focusEmployeeId]);

    useFocusEffect(React.useCallback(() => { loadCompanyLogo(); }, []));
  const loadCompanyLogo = async () => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) { const p = JSON.parse(cached); setCompanyLogo(p.logo || null); if (p.name || p.company_name) setCompanyName(p.name || p.company_name); }
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        setCompanyId(cid);
        const r = await api.get(EP.companyProfile, { params: { company_id: cid } });
        if (r.data?.success && r.data.data?.length > 0) { const lp = r.data.data[0]; setCompanyLogo(lp.logo || null); if (lp.name || lp.company_name) setCompanyName(lp.name || lp.company_name); await AsyncStorage.setItem('company_profile', JSON.stringify(lp)); }
      }
    } catch (e) { console.warn('Logo error:', e.message); }
  };

const loadEmployees = async () => {
    try {
      const cid = companyId || await AsyncStorage.getItem('company_id') || '1';
      const [r0, r1] = await Promise.allSettled([
        // Hired offers + active employees — both contribute to the "Hired"
        // pipeline view. An employee row is the post-hire source of truth
        // (created by AcceptedOffersScreen.handleMoveToHired); the offer
        // row remains useful for showing pipeline history if the employees
        // row wasn't created yet.
        api.get(EP.offers,    { params: { company_id: parseInt(cid), limit: 500 } }).catch(() => null),
        api.get(EP.employees, { params: { company_id: parseInt(cid), limit: 500 } }).catch(() => null),
      ]);
      const apiOffers = safeData(r0.status === 'fulfilled' ? r0.value : null)
        .filter(o => (o.status || '').toLowerCase() === 'hired');
      const apiEmps = safeData(r1.status === 'fulfilled' ? r1.value : null)
        .filter(e => (e.status || '').toLowerCase() === 'active');

      // Build the unified list. We index by email to merge: when an employee
      // row exists for an offer, the employee data wins (richer record).
      // Falls back to the offer row when the employees row hasn't been
      // created (legacy hires, or if employee-create POST failed).
      const byEmail = new Map();
      for (const o of apiOffers) {
        const email = (o.candidate_email || o.applicant_email || '').toLowerCase();
        if (!email) continue;
        byEmail.set(email, {
          id: 'offer_' + o.id,
          offerId: o.id,
          applicationId: o.job_application_id || null,
          userId: o.user_id || null,
          name: o.candidate_name || o.applicant_name || 'Unknown',
          email: o.candidate_email || o.applicant_email || '',
          phone: o.candidate_phone || o.applicant_phone || '',
          position: o.position || o.job_title || '',
          department: o.department || 'General',
          salary: o.offered_salary || o.salary || '',
          salaryCurrency: o.salary_currency || 'GBP',
          startDate: o.start_date || o.onboarding_date || o.created_at || new Date().toISOString(),
          location: o.location || '',
          status: 'hired',
          contractType: o.contract_type || 'Permanent',
          workType: o.work_type || 'Hybrid',
          source: 'offer',
        });
      }
      for (const e of apiEmps) {
        const email = (e.email || '').toLowerCase();
        if (!email) continue;
        // Employee row is the canonical record post-hire — overwrites
        // any offer-derived placeholder.
        byEmail.set(email, {
          id: 'emp_' + e.id,
          employeeId: e.id,
          applicationId: e.application_id || e.job_application_id || null,
          jobOfferId: e.job_offer_id || null,
          userId: e.user_id || null,
          name: e.name || e.employee_name || e.full_name || 'Unknown',
          email: e.email || '',
          phone: e.phone || '',
          position: e.position || e.job_title || '',
          department: e.department || '',
          salary: e.salary || '',
          startDate: e.start_date || e.created_at || new Date().toISOString(),
          location: e.location || '',
          status: 'hired',
          contractType: e.contract_type || 'Permanent',
          workType: e.work_type || 'Hybrid',
          source: 'employee',
        });
      }

      const mapped = Array.from(byEmail.values());
      setEmployees(mapped);
      await AsyncStorage.setItem('hired_employees', JSON.stringify(mapped));
    } catch (e) {
      console.warn('HiredScreen load:', e?.message);
      // No mock fallback — show empty state instead of fake data.
      try {
        const cached = await AsyncStorage.getItem('hired_employees');
        if (cached) setEmployees(JSON.parse(cached));
        else setEmployees([]);
      } catch { setEmployees([]); }
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  const startRealTimeUpdates = () => {
    // Simulate real-time updates every 30 seconds
    const interval = setInterval(() => {
      setRealTimeData(prev => ({
        ...prev,
        lastUpdate: new Date().toISOString(),
        activeUsers: Math.floor(Math.random() * 2) + 1, // 1-2 active users
      }));
    }, 30000);

    return () => clearInterval(interval);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadEmployees();
    setRefreshing(false);
  };

  const filteredEmployees = employees.filter(employee => {
    const matchesTab = selectedTab === 'all' || employee.currentStatus === selectedTab;
    
    const matchesSearch = (employee.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (employee.position || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (employee.department || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (employee.employeeId || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (employee.manager || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (employee.buddy || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
                         employee.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesTab && matchesSearch;
  });

  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    switch (sortBy) {
      case 'start_date':
        return new Date(b.startDate) - new Date(a.startDate);
      case 'performance':
        return b.performanceScore - a.performanceScore;
      case 'satisfaction':
        return b.satisfactionScore - a.satisfactionScore;
      case 'name':
        return a.name.localeCompare(b.name);
      case 'onboarding_progress':
        return b.onboardingProgress - a.onboardingProgress;
      default:
        return 0;
    }
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'first_week': return '#10B981';
      case 'onboarding': return '#3B82F6';
      case 'probation': return '#F59E0B';
      case 'established': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return '#10B981';
    if (score >= 80) return '#3B82F6';
    if (score >= 70) return '#F59E0B';
    return '#EF4444';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <MaterialCommunityIcons
          key={i}
          name={i <= rating ? 'star' : 'star-outline'}
          size={14}
          color={i <= rating ? '#F59E0B' : '#D1D5DB'}
        />
      );
    }
    return stars;
  };

  const handleEmployeePress = (employee) => {
    navigation.navigate('EmployeeDetailsScreen', { employee });
  };

  const handleSelectEmployee = (employeeId) => {
    setSelectedEmployees(prev => {
      if (prev.includes(employeeId)) {
        return prev.filter(id => id !== employeeId);
      } else {
        return [...prev, employeeId];
      }
    });
  };

  const handleViewRealTimeTracking = (employee) => {
    setSelectedEmployee(employee);
    setShowTrackingModal(true);
  };

    const handleStartOnboarding = async (employee) => {
    Alert.alert('Start Onboarding', 'Begin onboarding for ' + employee.name + '?', [{ text: 'Cancel' }, { text: 'Start', onPress: async () => {
      try { await api.put(EP.offers + '/' + employee.id, { status: 'onboarding' }); } catch {}
      Alert.alert('Started', 'Onboarding initiated for ' + employee.name);
    } }]);
  };

  const handleConvertToEmployee = async (employee) => {
    Alert.alert('Add to Employees', 'Add ' + employee.name + ' to employee records?', [{ text: 'Cancel' }, { text: 'Add', onPress: async () => {
      try { await api.post(EP.hireCandidate, { candidate_name: employee.name, candidate_email: employee.email, candidate_phone: employee.phone, job_title: employee.position, department: employee.department, salary: employee.salary, start_date: employee.startDate, application_id: employee.applicationId || null, company_id: parseInt(companyId) || 0 }); } catch (e) { console.warn('Hire failed:', e.message); }
      Alert.alert('Added', employee.name + ' added as employee', [{ text: 'OK' }]);
    } }]);
  };

  const handleEvaluate = (employee) => {
    navigation.navigate('EvaluationAssessmentScreen', { companyLogo, companyName, employeeId: employee.id, employeeName: employee.name, employeePosition: employee.position, employeeDepartment: employee.department, employeeEmail: employee.email });
  };

  const handleSendWelcome = (employee) => {
    if (employee.email) { const { Linking } = require('react-native'); Linking.openURL('mailto:' + employee.email + '?subject=Welcome to ' + encodeURIComponent(companyName || 'the team') + '&body=Dear ' + encodeURIComponent(employee.name) + ',%0A%0AWelcome! Your start date is ' + encodeURIComponent(employee.startDate ? new Date(employee.startDate).toLocaleDateString('en-GB') : 'TBC') + '.%0A%0ABest regards,%0A' + encodeURIComponent(companyName || 'HR Team')); }
    else Alert.alert('No Email', 'No email available for ' + employee.name);
  };

const handleBulkAction = (action) => {
    if (selectedEmployees.length === 0) {
      Alert.alert('No Selection', 'Please select employees first');
      return;
    }

    Alert.alert(
      'Bulk Action',
      `${action} ${selectedEmployees.length} selected employees?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { 
          text: 'Confirm', 
          onPress: async () => {
            for (const eid of selectedEmployees) { try { await api.put(EP.offers + '/' + eid, { status: action === 'Complete Onboarding' ? 'active' : action.toLowerCase().replace(/\s+/g, '_') }); } catch {} }
            setSelectedEmployees([]);
          }
        },
      ]
    );
  };

  const renderEmployeeCard = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.employeeCard,
        selectedEmployees.includes(item.id) && styles.selectedCard,
        // Soft highlight for the just-hired candidate arriving from
        // AcceptedOffersScreen → "Move to Hired".
        focusedId === item.id && {
          borderLeftWidth: 4,
          borderLeftColor: '#10B981',
          backgroundColor: '#F0FDF4',
        },
      ]}
      onPress={() => handleEmployeePress(item)}
      activeOpacity={0.7}
    >
      {/* Selection Checkbox */}
      <TouchableOpacity
        style={styles.checkbox}
        onPress={() => handleSelectEmployee(item.id)}
      >
        <MaterialCommunityIcons
          name={selectedEmployees.includes(item.id) ? 'checkbox-marked' : 'checkbox-blank-outline'}
          size={24}
          color={selectedEmployees.includes(item.id) ? '#06B6D4' : '#D1D5DB'}
        />
      </TouchableOpacity>

      {/* Online Status Indicator */}
      {item.isOnline && (
        <View style={styles.onlineIndicator}>
          <View style={styles.onlineDot} />
        </View>
      )}

      {/* Employee Header */}
      <View style={styles.employeeHeader}>
        <Image source={{ uri: (item.avatar && item.avatar.startsWith('http')) ? item.avatar : undefined }} style={styles.avatar} />
        <View style={styles.employeeInfo}>
          <View style={styles.employeeNameContainer}>
            <Text style={styles.employeeName}>{item.name}</Text>
            <Text style={styles.employeeId}>#{item.employeeId}</Text>
            <View style={styles.employeeBadges}>
              {(item.tags || []).map((tag, index) => (
                <View key={index} style={styles.tagBadge}>
                  <MaterialCommunityIcons name="pulse" size={10} color="#06B6D4" />
                  <Text style={styles.tagText}>{tag}</Text>
                </View>
              ))}
            </View>
          </View>
          <Text style={styles.employeePosition}>{item.position}</Text>
          <Text style={styles.employeeDepartment}>{item.department} • Started {formatTimeAgo(item.startDate)}</Text>
        </View>
        <View style={styles.employeeStatus}>
          <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.currentStatus) }]}>
            <Text style={styles.statusText}>{item.statusText}</Text>
          </View>
          <Text style={styles.daysWorked}>{item.daysWorked} days</Text>
        </View>
      </View>

      {/* Real-time Tracking Summary */}
      <View style={styles.trackingSummaryContainer}>
        <View style={styles.trackingSummaryHeader}>
          <MaterialCommunityIcons name="pulse" size={20} color="#06B6D4" />
          <Text style={styles.trackingSummaryTitle}>Real-time Tracking</Text>
          <View style={styles.overallScoreBadge}>
            <Text style={styles.overallScoreText}>{item.overallScore}%</Text>
          </View>
        </View>
        
        {/* Tracking Metrics Grid */}
        <View style={styles.trackingMetricsGrid}>
          <View style={styles.trackingMetric}>
            <MaterialCommunityIcons name="school" size={16} color="#3B82F6" />
            <Text style={styles.trackingMetricLabel}>Onboarding</Text>
            <Text style={[styles.trackingMetricValue, { color: getScoreColor(item.onboardingProgress) }]}>
              {item.onboardingProgress}%
            </Text>
          </View>
          <View style={styles.trackingMetric}>
            <MaterialCommunityIcons name="account-group" size={16} color="#8B5CF6" />
            <Text style={styles.trackingMetricLabel}>Team Integration</Text>
            <Text style={[styles.trackingMetricValue, { color: getScoreColor(item.teamIntegrationScore) }]}>
              {item.teamIntegrationScore}%
            </Text>
          </View>
          <View style={styles.trackingMetric}>
            <MaterialCommunityIcons name="trending-up" size={16} color="#F59E0B" />
            <Text style={styles.trackingMetricLabel}>Performance</Text>
            <Text style={[styles.trackingMetricValue, { color: getScoreColor(item.performanceScore) }]}>
              {item.performanceScore}%
            </Text>
          </View>
          <View style={styles.trackingMetric}>
            <MaterialCommunityIcons name="heart" size={16} color="#EF4444" />
            <Text style={styles.trackingMetricLabel}>Satisfaction</Text>
            <Text style={[styles.trackingMetricValue, { color: getScoreColor(item.satisfactionScore) }]}>
              {item.satisfactionScore}%
            </Text>
          </View>
        </View>
      </View>

      {/* Onboarding Progress */}
      <View style={styles.onboardingContainer}>
        <View style={styles.onboardingHeader}>
          <MaterialCommunityIcons name="clipboard-check" size={18} color="#3B82F6" />
          <Text style={styles.onboardingTitle}>Onboarding Progress</Text>
          <Text style={styles.onboardingProgress}>
            {item.completedMilestones}/{item.totalMilestones} milestones
          </Text>
        </View>
        
        {/* Progress Bar */}
        <View style={styles.progressBarContainer}>
          <View style={styles.progressBar}>
            <View style={[
              styles.progressFill,
              { width: `${item.onboardingProgress}%`, backgroundColor: getScoreColor(item.onboardingProgress) }
            ]} />
          </View>
          <Text style={styles.progressText}>{item.onboardingProgress}%</Text>
        </View>
        
        {/* Recent Milestones */}
        <View style={styles.milestonesContainer}>
          {(item.onboardingMilestones || []).slice(0, 3).map((milestone, index) => (
            <View key={milestone.id} style={styles.milestoneItem}>
              <MaterialCommunityIcons
                name={milestone.completed ? 'check-circle' : 'clock-outline'}
                size={14}
                color={milestone.completed ? '#10B981' : '#D1D5DB'}
              />
              <Text style={[
                styles.milestoneText,
                { color: milestone.completed ? '#374151' : '#9CA3AF' }
              ]}>
                {milestone.name}
              </Text>
              {milestone.completed && (
                <Text style={styles.milestoneDate}>
                  {formatDate(milestone.completedDate)}
                </Text>
              )}
            </View>
          ))}
        </View>
      </View>

      {/* Team Integration */}
      <View style={styles.teamIntegrationContainer}>
        <View style={styles.teamIntegrationHeader}>
          <MaterialCommunityIcons name="account-group" size={18} color="#8B5CF6" />
          <Text style={styles.teamIntegrationTitle}>Team Integration</Text>
          <Text style={styles.teamIntegrationScore}>
            {(item.teamIntegrationDetails || {}).metMembers || 0}/{(item.teamIntegrationDetails || {}).totalMembers || 0} met
          </Text>
        </View>
        
        <View style={styles.teamIntegrationDetails}>
          <View style={styles.teamIntegrationItem}>
            <MaterialCommunityIcons name="account-tie" size={14} color="#8B5CF6" />
            <Text style={styles.teamIntegrationLabel}>Manager:</Text>
            <Text style={styles.teamIntegrationValue}>{(item.manager || '').split(' - ')[0] || 'TBA'}</Text>
          </View>
          <View style={styles.teamIntegrationItem}>
            <MaterialCommunityIcons name="account-heart" size={14} color="#10B981" />
            <Text style={styles.teamIntegrationLabel}>Buddy:</Text>
            <Text style={styles.teamIntegrationValue}>{(item.buddy || '').split(' - ')[0] || 'TBA'}</Text>
          </View>
          <View style={styles.teamIntegrationItem}>
            <MaterialCommunityIcons name="account-multiple" size={14} color="#F59E0B" />
            <Text style={styles.teamIntegrationLabel}>Team Members:</Text>
            <Text style={styles.teamIntegrationValue}>
              {(item.teamIntegrationDetails || {}).metMembers || 0} of {(item.teamIntegrationDetails || {}).totalMembers || 0}
            </Text>
          </View>
        </View>
      </View>

      {/* Performance Indicators */}
      <View style={styles.performanceContainer}>
        <View style={styles.performanceHeader}>
          <MaterialCommunityIcons name="trending-up" size={18} color="#F59E0B" />
          <Text style={styles.performanceTitle}>Performance Indicators</Text>
        </View>
        
        <View style={styles.performanceIndicators}>
          {(item.performanceIndicators || []).map((indicator, index) => (
            <View key={indicator.id} style={styles.performanceIndicator}>
              <View style={styles.performanceIndicatorHeader}>
                <Text style={styles.performanceIndicatorName}>{indicator.name}</Text>
                <View style={styles.performanceIndicatorTrend}>
                  <MaterialCommunityIcons
                    name={indicator.trend === 'up' ? 'trending-up' : 'trending-neutral'}
                    size={12}
                    color={indicator.trend === 'up' ? '#10B981' : '#6B7280'}
                  />
                  <Text style={[
                    styles.performanceIndicatorValue,
                    { color: getScoreColor(indicator.value) }
                  ]}>
                    {indicator.value}%
                  </Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Recent Activities */}
      <View style={styles.activitiesContainer}>
        <View style={styles.activitiesHeader}>
          <MaterialCommunityIcons name="history" size={18} color="#6B7280" />
          <Text style={styles.activitiesTitle}>Recent Activities</Text>
          <Text style={styles.lastActivity}>Last: {formatTimeAgo(item.lastActivity)}</Text>
        </View>
        
        <View style={styles.activitiesList}>
          {(item.recentActivities || []).slice(0, 2).map((activity, index) => (
            <View key={activity.id} style={styles.activityItem}>
              <MaterialCommunityIcons
                name={activity.icon}
                size={14}
                color={activity.color}
              />
              <View style={styles.activityContent}>
                <Text style={styles.activityTitle}>{activity.title}</Text>
                <Text style={styles.activityTime}>{formatTimeAgo(activity.timestamp)}</Text>
              </View>
            </View>
          ))}
        </View>
      </View>

      {/* Goals and Training */}
      <View style={styles.goalsTrainingContainer}>
        <View style={styles.goalsTrainingHeader}>
          <MaterialCommunityIcons name="target" size={16} color="#10B981" />
          <Text style={styles.goalsTrainingTitle}>Goals & Training</Text>
        </View>
        
        <View style={styles.goalsTrainingGrid}>
          <View style={styles.goalsTrainingItem}>
            <MaterialCommunityIcons name="flag" size={14} color="#10B981" />
            <Text style={styles.goalsTrainingLabel}>Active Goals</Text>
            <Text style={styles.goalsTrainingValue}>{(item.goals || []).length}</Text>
          </View>
          <View style={styles.goalsTrainingItem}>
            <MaterialCommunityIcons name="school" size={14} color="#3B82F6" />
            <Text style={styles.goalsTrainingLabel}>Training</Text>
            <Text style={styles.goalsTrainingValue}>
              {item.completedTraining}/{item.totalTraining}
            </Text>
          </View>
        </View>
      </View>

      {/* Success & Risk Factors */}
      {((item.successIndicators || []).length > 0 || (item.riskFactors || []).length > 0) && (
        <View style={styles.factorsContainer}>
          {(item.successIndicators || []).length > 0 && (
            <View style={styles.successFactors}>
              <Text style={styles.factorsTitle}>Success Indicators:</Text>
              <View style={styles.factorsList}>
                {(item.successIndicators || []).map((factor, index) => (
                  <View key={index} style={styles.successFactor}>
                    <MaterialCommunityIcons name="check-circle" size={12} color="#10B981" />
                    <Text style={styles.successFactorText}>{factor}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
          
          {(item.riskFactors || []).length > 0 && (
            <View style={styles.riskFactors}>
              <Text style={styles.factorsTitle}>Risk Factors:</Text>
              <View style={styles.factorsList}>
                {(item.riskFactors || []).map((factor, index) => (
                  <View key={index} style={styles.riskFactor}>
                    <MaterialCommunityIcons name="alert-circle" size={12} color="#EF4444" />
                    <Text style={styles.riskFactorText}>{factor}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}
        </View>
      )}

      {/* Employment Summary */}
      <View style={styles.employmentSummaryContainer}>
        <Text style={styles.employmentSummaryTitle}>Employment Summary</Text>
        <View style={styles.employmentSummaryGrid}>
          <View style={styles.employmentSummaryItem}>
            <Text style={styles.employmentSummaryLabel}>Start Date</Text>
            <Text style={styles.employmentSummaryValue}>{formatDate(item.startDate)}</Text>
          </View>
          <View style={styles.employmentSummaryItem}>
            <Text style={styles.employmentSummaryLabel}>Package</Text>
            <Text style={styles.employmentSummaryValue}>{formatCurrency(item.totalPackage)}</Text>
          </View>
          <View style={styles.employmentSummaryItem}>
            <Text style={styles.employmentSummaryLabel}>Last Seen</Text>
            <Text style={[
              styles.employmentSummaryValue,
              { color: item.isOnline ? '#10B981' : '#6B7280' }
            ]}>
              {item.isOnline ? 'Online' : formatTimeAgo(item.lastSeen)}
            </Text>
          </View>
          <View style={styles.employmentSummaryItem}>
            <Text style={styles.employmentSummaryLabel}>Overall Rating</Text>
            <View style={styles.starsContainer}>
              {renderStars((item.recruitmentJourney || {}).overallRating || 0)}
            </View>
          </View>
        </View>
      </View>

      {/* Actions */}
      <View style={styles.employeeActions}>
        <TouchableOpacity
          style={[styles.actionButton, { backgroundColor: '#06B6D4' }]}
          onPress={() => handleViewRealTimeTracking(item)}
        >
          <MaterialCommunityIcons name="pulse" size={16} color="#FFFFFF" />
          <Text style={[styles.actionButtonText, { color: '#FFFFFF' }]}>Real-time Tracking</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => navigation.navigate('EmployeeDetailsScreen', { employee: item, companyLogo, companyName })}
        >
          <MaterialCommunityIcons name="eye" size={16} color="#06B6D4" />
          <Text style={styles.actionButtonText}>View Details</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.moreButton}
          onPress={() => {
            Alert.alert(
              item.name || 'Employee',
              'Choose an action',
              [
                { text: 'Start Onboarding', onPress: () => handleStartOnboarding(item) },
                { text: 'Add to Employees', onPress: () => handleConvertToEmployee(item) },
                { text: 'Evaluate', onPress: () => handleEvaluate(item) },
                { text: 'Send Welcome Email', onPress: () => handleSendWelcome(item) },
                { text: 'Cancel', style: 'cancel' },
              ]
            );
          }}
        >
          <MaterialCommunityIcons name="dots-vertical" size={20} color="#6B7280" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#06B6D4" />
          <Text style={styles.loadingText}>Loading hired employees...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#06B6D4" />
      
      {/* Header */}
      <LinearGradient
        colors={['#06B6D4', '#0891B2']}
        style={styles.header}
      >
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
          </TouchableOpacity>
          
          <View style={styles.headerInfo}>
            <View style={styles.headerTitleContainer}>
              {companyLogo ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 24, height: 24, borderRadius: 12 }} /> : <MaterialCommunityIcons name="account-check" size={24} color="#FFFFFF" />}
              <Text style={styles.headerTitle}>Hired</Text>
            </View>
            <Text style={styles.headerSubtitle}>
              {sortedEmployees.length} Employees • Real-time Tracking
            </Text>
          </View>
          
          <TouchableOpacity
            style={styles.trackingButton}
            onPress={() => setShowTrackingModal(true)}
          >
            <MaterialCommunityIcons name="pulse" size={24} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      {/* Search and Sort */}
      <View style={styles.searchSortContainer}>
        <View style={styles.searchBar}>
          <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
          <TextInput
            style={styles.searchInput}
            placeholder="Search hired employees..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholderTextColor="#9CA3AF"
          />
        </View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.sortContainer}
        >
          {sortOptions.map((option) => (
            <TouchableOpacity
              key={option.id}
              style={[
                styles.sortOption,
                sortBy === option.id && styles.activeSortOption,
              ]}
              onPress={() => setSortBy(option.id)}
            >
              <MaterialCommunityIcons
                name={option.icon}
                size={16}
                color={sortBy === option.id ? '#FFFFFF' : '#6B7280'}
              />
              <Text
                style={[
                  styles.sortOptionText,
                  sortBy === option.id && styles.activeSortOptionText,
                ]}
              >
                {option.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Tabs */}
      <View style={styles.tabsContainer}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.tabsContent}
        >
          {tabs.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                selectedTab === tab.id && styles.activeTab,
              ]}
              onPress={() => setSelectedTab(tab.id)}
            >
              <Text
                style={[
                  styles.tabText,
                  selectedTab === tab.id && styles.activeTabText,
                ]}
              >
                {tab.name}
              </Text>
              <View
                style={[
                  styles.tabBadge,
                  selectedTab === tab.id && styles.activeTabBadge,
                ]}
              >
                <Text
                  style={[
                    styles.tabBadgeText,
                    selectedTab === tab.id && styles.activeTabBadgeText,
                  ]}
                >
                  {selectedTab === tab.id ? sortedEmployees.length : tab.count}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Bulk Actions */}
      {selectedEmployees.length > 0 && (
        <View style={styles.bulkActionsContainer}>
          <Text style={styles.bulkActionsText}>
            {selectedEmployees.length} selected
          </Text>
          <View style={styles.bulkActions}>
            <TouchableOpacity
              style={styles.bulkActionButton}
              onPress={() => handleBulkAction('Schedule Check-in')}
            >
              <MaterialCommunityIcons name="calendar-plus" size={16} color="#FFFFFF" />
              <Text style={styles.bulkActionText}>Schedule Check-in</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.bulkActionButton, { backgroundColor: '#10B981' }]}
              onPress={() => handleBulkAction('Export Data')}
            >
              <MaterialCommunityIcons name="download" size={16} color="#FFFFFF" />
              <Text style={styles.bulkActionText}>Export Data</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Employees List */}
      <FlatList
        ref={flatListRef}
        data={sortedEmployees}
        keyExtractor={(item) => String(item.id)}
        renderItem={renderEmployeeCard}
        style={styles.employeesList}
        contentContainerStyle={styles.employeesListContent}
        showsVerticalScrollIndicator={false}
        onScrollToIndexFailed={(info) => {
          // FlatList virtualisation may not have rendered the target row yet.
          // Wait a tick and retry.
          setTimeout(() => {
            flatListRef.current?.scrollToOffset?.({
              offset: info.averageItemLength * info.index,
              animated: true,
            });
          }, 200);
        }}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
        ListEmptyComponent={() => (
          <View style={styles.emptyContainer}>
            <MaterialCommunityIcons name="account-check-outline" size={64} color="#D1D5DB" />
            <Text style={styles.emptyTitle}>No hired employees found</Text>
            <Text style={styles.emptySubtitle}>
              {searchQuery
                ? 'Try adjusting your search criteria'
                : 'No employees match the current criteria'}
            </Text>
          </View>
        )}
      />

      {/* Real-time Status Bar */}
      <View style={styles.realTimeStatusBar}>
        <View style={styles.realTimeIndicator}>
          <View style={styles.pulseDot} />
          <Text style={styles.realTimeText}>Real-time Tracking Active</Text>
        </View>
        <Text style={styles.lastUpdateText}>
          Last update: {realTimeData.lastUpdate ? formatTimeAgo(realTimeData.lastUpdate) : 'Just now'}
        </Text>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#6B7280',
  },
  header: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  headerSubtitle: {
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 4,
  },
  trackingButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  searchSortContainer: {
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 12,
  },
  searchInput: {
    flex: 1,
    marginLeft: 12,
    fontSize: 16,
    color: '#1F2937',
  },
  sortContainer: {
    flexDirection: 'row',
  },
  sortOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 16,
    backgroundColor: '#F3F4F6',
  },
  activeSortOption: {
    backgroundColor: '#06B6D4',
  },
  sortOptionText: {
    marginLeft: 4,
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  activeSortOptionText: {
    color: '#FFFFFF',
  },
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  tabsContent: {
    paddingHorizontal: 20,
  },
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
  },
  activeTab: {
    backgroundColor: '#CFFAFE',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#6B7280',
  },
  activeTabText: {
    color: '#06B6D4',
  },
  tabBadge: {
    marginLeft: 8,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    backgroundColor: '#E5E7EB',
  },
  activeTabBadge: {
    backgroundColor: '#06B6D4',
  },
  tabBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6B7280',
  },
  activeTabBadgeText: {
    color: '#FFFFFF',
  },
  bulkActionsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#06B6D4',
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  bulkActionsText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  bulkActions: {
    flexDirection: 'row',
  },
  bulkActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F59E0B',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginLeft: 8,
  },
  bulkActionText: {
    marginLeft: 4,
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  employeesList: {
    flex: 1,
  },
  employeesListContent: {
    padding: 20,
    paddingBottom: 80, // Space for real-time status bar
  },
  employeeCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3.84,
    elevation: 5,
  },
  selectedCard: {
    borderWidth: 2,
    borderColor: '#06B6D4',
  },
  checkbox: {
    position: 'absolute',
    top: 12,
    right: 12,
    zIndex: 1,
  },
  onlineIndicator: {
    position: 'absolute',
    top: 12,
    right: 50,
    zIndex: 1,
  },
  onlineDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    backgroundColor: '#10B981',
    shadowColor: '#10B981',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
    elevation: 4,
  },
  employeeHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    marginRight: 12,
  },
  employeeInfo: {
    flex: 1,
  },
  employeeNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginBottom: 4,
  },
  employeeName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
    marginRight: 8,
  },
  employeeId: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: 'monospace',
    marginRight: 8,
  },
  employeeBadges: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  tagBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#CFFAFE',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  tagText: {
    marginLeft: 2,
    fontSize: 10,
    fontWeight: '600',
    color: '#06B6D4',
  },
  employeePosition: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 2,
  },
  employeeDepartment: {
    fontSize: 12,
    color: '#6B7280',
  },
  employeeStatus: {
    alignItems: 'flex-end',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginBottom: 4,
  },
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  daysWorked: {
    fontSize: 11,
    color: '#6B7280',
  },
  trackingSummaryContainer: {
    backgroundColor: '#CFFAFE',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#06B6D4',
  },
  trackingSummaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  trackingSummaryTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  overallScoreBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#06B6D4',
    borderRadius: 12,
  },
  overallScoreText: {
    fontSize: 12,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  trackingMetricsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
  },
  trackingMetric: {
    alignItems: 'center',
  },
  trackingMetricLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  trackingMetricValue: {
    fontSize: 11,
    fontWeight: 'bold',
    marginTop: 2,
  },
  onboardingContainer: {
    backgroundColor: '#EFF6FF',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  onboardingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  onboardingTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  onboardingProgress: {
    fontSize: 12,
    color: '#3B82F6',
    fontWeight: '600',
  },
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  progressBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#E5E7EB',
    borderRadius: 3,
    marginRight: 8,
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    minWidth: 35,
  },
  milestonesContainer: {
    marginTop: 4,
  },
  milestoneItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  milestoneText: {
    marginLeft: 8,
    fontSize: 12,
    fontWeight: '500',
    flex: 1,
  },
  milestoneDate: {
    fontSize: 10,
    color: '#6B7280',
  },
  teamIntegrationContainer: {
    backgroundColor: '#F3E8FF',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  teamIntegrationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  teamIntegrationTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  teamIntegrationScore: {
    fontSize: 12,
    color: '#8B5CF6',
    fontWeight: '600',
  },
  teamIntegrationDetails: {
    marginTop: 4,
  },
  teamIntegrationItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  teamIntegrationLabel: {
    marginLeft: 8,
    fontSize: 12,
    color: '#6B7280',
    width: 80,
  },
  teamIntegrationValue: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  performanceContainer: {
    backgroundColor: '#FEF3E2',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  performanceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  performanceTitle: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  performanceIndicators: {
    marginTop: 4,
  },
  performanceIndicator: {
    marginBottom: 6,
  },
  performanceIndicatorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  performanceIndicatorName: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  performanceIndicatorTrend: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  performanceIndicatorValue: {
    marginLeft: 4,
    fontSize: 12,
    fontWeight: '600',
  },
  activitiesContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  activitiesHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  activitiesTitle: {
    flex: 1,
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  lastActivity: {
    fontSize: 12,
    color: '#6B7280',
  },
  activitiesList: {
    marginTop: 4,
  },
  activityItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  activityContent: {
    marginLeft: 8,
    flex: 1,
  },
  activityTitle: {
    fontSize: 12,
    color: '#374151',
    fontWeight: '500',
  },
  activityTime: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  goalsTrainingContainer: {
    backgroundColor: '#F0FDF4',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  goalsTrainingHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  goalsTrainingTitle: {
    marginLeft: 8,
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  goalsTrainingGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 4,
  },
  goalsTrainingItem: {
    alignItems: 'center',
  },
  goalsTrainingLabel: {
    fontSize: 10,
    color: '#6B7280',
    marginTop: 2,
  },
  goalsTrainingValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#1F2937',
    marginTop: 2,
  },
  factorsContainer: {
    marginBottom: 12,
  },
  successFactors: {
    marginBottom: 8,
  },
  riskFactors: {
    marginBottom: 4,
  },
  factorsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 4,
  },
  factorsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  successFactor: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#D1FAE5',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  successFactorText: {
    marginLeft: 2,
    fontSize: 10,
    color: '#10B981',
  },
  riskFactor: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 6,
    paddingVertical: 2,
    backgroundColor: '#FEE2E2',
    borderRadius: 8,
    marginRight: 4,
    marginBottom: 2,
  },
  riskFactorText: {
    marginLeft: 2,
    fontSize: 10,
    color: '#EF4444',
  },
  employmentSummaryContainer: {
    backgroundColor: '#F9FAFB',
    borderRadius: 12,
    padding: 12,
    marginBottom: 12,
  },
  employmentSummaryTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
  },
  employmentSummaryGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  employmentSummaryItem: {
    alignItems: 'center',
  },
  employmentSummaryLabel: {
    fontSize: 10,
    color: '#6B7280',
  },
  employmentSummaryValue: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#374151',
    marginTop: 2,
  },
  starsContainer: {
    flexDirection: 'row',
    marginTop: 2,
  },
  employeeActions: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: '#F3F4F6',
    borderRadius: 12,
    marginRight: 8,
    marginBottom: 4,
  },
  actionButtonText: {
    marginLeft: 4,
    fontSize: 11,
    color: '#6B7280',
    fontWeight: '500',
  },
  moreButton: {
    padding: 6,
    marginLeft: 'auto',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#374151',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingHorizontal: 40,
  },
  realTimeStatusBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: '#06B6D4',
    paddingHorizontal: 20,
    paddingVertical: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  realTimeIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
    marginRight: 8,
    shadowColor: '#FFFFFF',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 4,
    elevation: 4,
  },
  realTimeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  lastUpdateText: {
    fontSize: 10,
    color: 'rgba(255, 255, 255, 0.8)',
  },
});

